import { CustomDataGrid } from "@app/components";
import { Box, Grid, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { GridColDef } from "@mui/x-data-grid";

export enum LABELS_TABLE_DRYING {
  PESO_SECADO_MUESTRA_CAPSULA = "Peso secado muestra + cápsula (g)",
  PESO_SECADO_MUESTRA = "Peso secado muestra (g)",
}

interface AnalysisFormProps {
  count: number;
  tara: any;
  setDryingValue: (value: number) => void;
}

const ChartDryingForm = ({
  count,
  tara,
  setDryingValue,
}: AnalysisFormProps) => {
  const { watch, setValue } = useFormContext();
  const rowsCurva = watch("rowsCurvaCalcinationDrying") || [];
  const rowsCurvaCalcination = watch("rowsCurvaCalcination") || [];
  const inputRef = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    if (!rowsCurva || rowsCurva.length === 0) {
      const initialRows = generateInitialRows(count);
      setValue("rowsCurvaCalcinationDrying", initialRows);
    }
  }, [rowsCurva, setValue, count]);

  useEffect(() => {
    if (rowsCurvaCalcination.length > 0) {
      recalculateDryingTable();
    }
  }, [rowsCurvaCalcination]);

  const recalculateDryingTable = () => {
    const taraActualizada = watch("rowsCurvaCalcination")?.find(
      (row) => row.label === "Tara cápsula (g)"
    );

    if (!taraActualizada) {
      console.warn("No se encontró la tara actualizada.");
      return;
    }

    const updatedRows = [
      ...watch("rowsCurvaCalcinationDrying")?.map((row) => ({ ...row })),
    ];

    const pesoSecadoCapsulaRow = updatedRows.find(
      (row) => row.label === LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA_CAPSULA
    );

    const pesoSecadoRow = updatedRows.find(
      (row) => row.label === LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA
    );

    if (pesoSecadoCapsulaRow && pesoSecadoRow) {
      let totalPesoSecado = 0;

      for (let i = 1; i <= count; i++) {
        const pesoTotalSecado =
          Number(pesoSecadoCapsulaRow[`capsule_${i}`]) || 0;
        const pesoTaraCapsula = Number(taraActualizada[`capsule_${i}`]) || 0;
        const pesoSecado = (pesoTotalSecado - pesoTaraCapsula).toFixed(2);

        pesoSecadoRow[`capsule_${i}`] = pesoSecado;
        totalPesoSecado += Number(pesoSecado);
      }

      pesoSecadoRow.total = totalPesoSecado.toFixed(2);
      setValue("rowsCurvaCalcinationDrying", [...updatedRows]);
    }
  };

  const generateInitialRows = (count: number) => {
    const rows = [
      {
        id: "secado_1",
        label: LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA_CAPSULA,
        ...Array.from({ length: count }, (_, i) => ({
          [`capsule_${i + 1}`]: "",
        })).reduce((acc, val, i) => ({ ...acc, [`capsule_${i + 1}`]: "" }), {}),
        total: 0,
      },
      {
        id: "secado_2",
        label: LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA,
        ...Array.from({ length: count }, (_, i) => ({
          [`capsule_${i + 1}`]: "",
        })).reduce((acc, val, i) => ({ ...acc, [`capsule_${i + 1}`]: "" }), {}),
        total: 0,
      },
    ];
    return rows;
  };

  const columnsDryingCurva: GridColDef[] = useMemo(() => {
    const dynamicColumns: GridColDef[] = [
      {
        field: "label",
        headerName: "Secado",
        headerAlign: "center",
        align: "left",
        flex: 1,
        editable: false,
      },
    ];

    for (let i = 1; i <= count; i++) {
      dynamicColumns.push({
        field: `capsule_${i}`,
        headerName: `Cápsula ${i}`,
        headerAlign: "center",
        align: "center",
        editable: true,
        flex: 1,
        renderCell: (params) => {
          const isDisabled =
            params.row.label === LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA;
          const valueAsNumber = Number(params.value);
          const isValidNumber =
            !isNaN(valueAsNumber) &&
            !isNaN(parseFloat(params.value)) &&
            typeof valueAsNumber === "number";

          return (
            <Box sx={{ width: "100%", display: "flex" }}>
              <TextField
                inputRef={(el) =>
                  (inputRef.current[`${params.id}_${params.field}`] = el)
                }
                type="text"
                value={params.value || ""}
                onChange={(e) => {
                  setValue(
                    `rowsCurvaCalcinationDrying.${params.id}.${params.field}`,
                    e.target.value
                  );
                }}
                style={{
                  width: "100%",
                  border: "none",
                  textAlign: "center",
                  color: isValidNumber ? "inherit" : "red",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab") {
                    e.preventDefault();

                    const keys = Object.keys(inputRef.current);
                    const currentIndex = keys.indexOf(
                      `${params.id}_${params.field}`
                    );

                    if (currentIndex !== -1) {
                      let nextIndex = currentIndex + 1;

                      while (
                        nextIndex < keys.length &&
                        inputRef.current[keys[nextIndex]]?.disabled
                      ) {
                        nextIndex++;
                      }

                      const nextInputKey = keys[nextIndex];

                      if (nextInputKey && inputRef.current[nextInputKey]) {
                        requestAnimationFrame(() => {
                          inputRef.current[nextInputKey]?.focus();
                        });
                      }
                    }
                  }
                }}
                fullWidth
                disabled={isDisabled}
                sx={{
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    boxSizing: "inherit",
                  },
                }}
              />
            </Box>
          );
        },
      });
    }

    dynamicColumns.push({
      field: "total",
      headerName: "Total",
      headerAlign: "center",
      align: "center",
      editable: false,
      flex: 1,
      renderCell: (params) => {
        const total = Number(params.row.total || 0);
        return <div style={{ fontWeight: "bold" }}>{total.toFixed(2)}</div>;
      },
    });

    return dynamicColumns;
  }, [count]);

  const processRowUpdate = (newRow) => {
    let updatedRows = rowsCurva.map((row) => ({ ...row }));

    let total = 0;

    if (newRow.label !== LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA) {
      for (let i = 1; i <= count; i++) {
        total += Number(newRow[`capsule_${i}`]) || 0;
      }
      newRow.total = total.toFixed(2);
    }

    updatedRows = updatedRows.map((row) =>
      row.id === newRow.id ? newRow : row
    );

    const pesoSecadoRow = updatedRows.find(
      (row) => row.label === "Peso secado muestra (g)"
    );

    const pesoSecadoCapsulaRow = updatedRows.find(
      (row) => row.label === LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA_CAPSULA
    );

    if (pesoSecadoRow && pesoSecadoCapsulaRow && tara) {
      let totalPesoSecado = 0;

      for (let i = 1; i <= count; i++) {
        const pesoTotalSecado =
          Number(pesoSecadoCapsulaRow[`capsule_${i}`]) || 0;
        const pesoTaraCapsula = Number(tara[`capsule_${i}`]) || 0;
        const pesoSecado = (pesoTotalSecado - pesoTaraCapsula).toFixed(2);

        pesoSecadoRow[`capsule_${i}`] = pesoSecado;
        totalPesoSecado += Number(pesoSecado);
      }

      pesoSecadoRow.total = totalPesoSecado.toFixed(2);
    }

    setValue("rowsCurvaCalcinationDrying", updatedRows);
    setTimeout(() => {
      const keys = Object.keys(inputRef.current);

      const currentFilter = Object.keys(newRow).filter((key) =>
        key.startsWith("capsule_")
      );

      const firstEmptyInput = currentFilter.find((key) => newRow[key] === "");

      if (firstEmptyInput) {
        const firstEmptyInputKey = keys.find(
          (key) => key === `${newRow.id}_${firstEmptyInput}`
        );

        if (firstEmptyInputKey && inputRef.current[firstEmptyInputKey]) {
          setTimeout(() => inputRef.current[firstEmptyInputKey]?.focus(), 0);
        }
      }
    }, 0);

    return newRow;
  };

  const totalPesoSecadoMuestra = useMemo(() => {
    const row = rowsCurva.find(
      (row) => row.label === LABELS_TABLE_DRYING.PESO_SECADO_MUESTRA
    );
    const value = row ? Number(row.total) : 0;
    //setDryingValue(value);
    return value;
  }, [rowsCurva]);

  useEffect(() => {
    setDryingValue(totalPesoSecadoMuestra);
  }, [totalPesoSecadoMuestra, setDryingValue]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CustomDataGrid
              key={JSON.stringify(rowsCurva)}
              rows={rowsCurva || []}
              columns={columnsDryingCurva}
              search={false}
              processRowUpdate={processRowUpdate}
              disablePagination={true}
              footerContent={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    fontWeight: "bold",
                    gap: 2,
                  }}
                >
                  <Typography variant="sectionTitle">
                    Peso seco todas las muestras (g):
                  </Typography>
                  <Typography variant="sectionTitle">
                    {totalPesoSecadoMuestra.toFixed(2)}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChartDryingForm;
